import {
  HierarchyNav,
  SIDEBAR_WIDTH
} from '@components/UI-Components/HierarchyNav/HierarchyNav'
import useHierarchyItemTableContext from '@components/UI-Components/HierarchyWiseItemsTable/hooks/use-hierarchy-item-table-context'
import { NoRecordFound } from '@components/UI-Components/NoRecordFound'
import { Divider } from '@material-ui/core'
import useOrderContext from '@modules/order/hooks/use-order-context'
import { SAChannelWiseProductDetails } from '@modules/showroom/productDetail/new/container'
import { stores } from '@stores'
import { nav } from '@stores/nav'
import cn from 'classnames'
import { useObserver } from 'mobx-react-lite'
import React, { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AutoSizer } from 'react-virtualized'
import { orderTemplatePreferencesStore } from '@modules/order/stores/order/OrderTemplatePreferencesStore'
import EnhancedHierarchyWiseItemsTable, {
  EnhancedHierarchyWiseItemsTableProps
} from '@components/UI-Components/HierarchyWiseItemsTable/EnhancedHierarchyWiseItemsTable'
import { useOrderStyles } from './OrderTableStyle'
import useViewContext from '@modules/order/hooks/use-view-context'
import { EnhancedListUIStore } from '@modules/showroom/EnhancedOrder/stores/EnhancedListView/EnhancedListUIStore'
import classNames from 'classnames'

const TOPBAR_HEIGHT = 42

interface EnhancedOrderTableViewProps
  extends Omit<
    EnhancedHierarchyWiseItemsTableProps,
    'dimensions' | 'onScroll' | 'setTableRef' | 'pageSize' | 'scrollIndex'
  > {
  hideHierarchy?: boolean
  headerRows?: any[]
  tableStoreInstance: any
  tableContextValue: any
}

const EnhancedOrderTableView: FC<EnhancedOrderTableViewProps> = ({
  tableStoreInstance,
  tableContextValue,
  ...props
}) =>
  useObserver(function useHook() {
    const { t } = useTranslation()
    const {
      store: {
        dataStore: { heirarchyLoading }
      }
    } = useHierarchyItemTableContext()

    const {
      nav: {
        queryParams: { orderId }
      }
    } = stores
    const { fetchOrderPreferences } = orderTemplatePreferencesStore
    const {
      uiStore: {
        dataStore: { getTableSetting }
      }
    } = useOrderContext()

    React.useEffect(() => {
      if (props?.selectedView) getTableSetting()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props?.selectedView, orderId])

    React.useEffect(() => {
      if (orderId) fetchOrderPreferences()
    }, [orderId, fetchOrderPreferences])

    return (
      <div style={{ flex: '1 1 100%', height: '100%', width: '100%' }}>
        {heirarchyLoading ? (
          t('common.label.loading', 'Loading...')
        ) : (
          <AutoSizer>
            {({ width, height }) => (
              <AutoSizerContent
                width={width}
                height={height}
                {...props}
                tableStoreInstance={tableStoreInstance}
                tableContextValue={tableContextValue}
              />
            )}
          </AutoSizer>
        )}
      </div>
    )
  })

const AutoSizerContent = ({
  width,
  height,
  tableStoreInstance,
  tableContextValue,
  ...props
}) =>
  useObserver(function useHook() {
    const {
      store: {
        dataStore: { heirarchyLoading, heirarchy },
        activeItemStore: { index: focusedItemIndex },
        scrollPositionStore: { onScroll, scrollIndex },
        elementRefStore,
        isSidebarOpen,
        navHierarchyDetails,
        currentHierarchy,
        setCurrentHierarchy,
        // Product Details
        removeItemObjectFromHierarchy,
        getFocusedProductHierarchy: getFocusedProductHeirarchy,
        isFilterOrSearchApplied,
        setActiveFocusProductIndex,
        onNextButtonClick,
        isNextProductAvailable,
        onPreviousButtonClick,
        isPreviousProductAvailable,
        updateRemovedProductResponseCache,
        isSearchActive,
        onProductRemovedFromProductDetails
      }
    } = useHierarchyItemTableContext()

    const viewContextVal = useViewContext()
    const {
      dataStore: { removeProductFromOrderDetailsPopup, addProuductToOrderAgain }
    } = viewContextVal.uiStore as EnhancedListUIStore<any>

    // Product Details
    const { uiStore: orderStore } = useOrderContext()

    const { isOrderEditable } = orderStore
    const summary = orderStore?.dataStore?.summary
    const classes = useOrderStyles()
    // Product Details
    const {
      queryParams: { bspId }
    } = nav
    const {
      productConfiguration: { enhancedViewEnabled }
    } = stores
    const prevNextAction = {
      nextAction: onNextButtonClick,
      isNextButtonActive: isNextProductAvailable,
      previousAction: onPreviousButtonClick,
      isPreviousButtonActive: isPreviousProductAvailable
    }
    const onBottomIconClickHandler = useCallback(() => {
      removeItemObjectFromHierarchy(
        focusedItemIndex === -1 ? 0 : focusedItemIndex,
        getFocusedProductHeirarchy(focusedItemIndex === -1 ? 0 : focusedItemIndex),
        false
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedItemIndex])

    // fix for GBB-9038: Get topbar height and calculate table container height based on it
    const topbarEle = document.querySelector('#SAOrderTopbar') as HTMLElement
    let CALC_TOPBAR_HEIGHT = topbarEle
      ? topbarEle.offsetHeight + TOPBAR_HEIGHT
      : TOPBAR_HEIGHT * 3
    const dimensions = useMemo(
      () => ({
        container: {
          height:
            isSearchActive && CALC_TOPBAR_HEIGHT > TOPBAR_HEIGHT * 3
              ? height - (CALC_TOPBAR_HEIGHT + 30)
              : height - CALC_TOPBAR_HEIGHT,
          width: isSidebarOpen ? width - SIDEBAR_WIDTH : width
        }
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [width, height, isSidebarOpen, isSearchActive, CALC_TOPBAR_HEIGHT]
    )

    return (
      <div style={{ width, height }}>
        <div className={classNames('enhanced-table', classes.productNavAndViewContainer)}>
          {heirarchyLoading || heirarchy?.length ? (
            <>
              <div
                style={{ width, height: height - CALC_TOPBAR_HEIGHT }}
                className={classes.productsViewContainer}
              >
                <div style={{ marginTop: enhancedViewEnabled ? 16 : 44 }}>
                  {isSidebarOpen && (
                    <>
                      <Divider orientation='horizontal' className={classes.divider} />
                      <HierarchyNav
                        open={isSidebarOpen}
                        title={''} //sidebarTitle
                        height={height - CALC_TOPBAR_HEIGHT}
                        hierarchy={navHierarchyDetails}
                        selected={currentHierarchy}
                        scrollIndex={scrollIndex}
                        onNavClick={({ leafNodeId }) => setCurrentHierarchy(leafNodeId)}
                        hierarchyLoading={heirarchyLoading}
                        isEnhancedViewEnabled
                        enhancedHeaderEnabled={false}
                      />
                    </>
                  )}
                </div>
                <div
                  className={cn(classes.content, {
                    [classes.contentSidebar]: isSidebarOpen
                  })}
                >
                  <EnhancedHierarchyWiseItemsTable
                    {...(props as any)}
                    onScroll={onScroll}
                    ref={elementRefStore.setRef}
                    dimensions={dimensions}
                    pageSize={10}
                    scrollIndex={scrollIndex}
                    actions={[]}
                    tableStoreInstance={tableStoreInstance}
                    tableContextValue={tableContextValue}
                  />
                </div>
              </div>
              {/* Changed: New Order Table */}
              {bspId && (
                <SAChannelWiseProductDetails
                  productDetailBottomRightCallBack={
                    ((bspId, res) => {
                      updateRemovedProductResponseCache(bspId, res)
                      onBottomIconClickHandler()
                    }) as any
                  }
                  prevNextAction={prevNextAction}
                  setActiveFocusProductIndex={setActiveFocusProductIndex}
                  showIcon={isOrderEditable}
                  isFilterOrSearchApplied={isFilterOrSearchApplied}
                  isBottomRightIconClickable={true}
                  onProductRemovedFromProductDetails={onProductRemovedFromProductDetails}
                  addProductToOrderHandler={productId => {
                    return addProuductToOrderAgain(productId, summary).then(resp => resp)
                  }}
                  removeProductFromOrderHandler={productId => {
                    return removeProductFromOrderDetailsPopup(productId).then(
                      resp => resp
                    )
                  }}
                />
              )}
            </>
          ) : (
            <NoRecordFound />
          )}
        </div>
      </div>
    )
  })

export default EnhancedOrderTableView
